// RechercheInforme.jsx

import React, { useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window'; 
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Grid,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Fab,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Tooltip,
  Autocomplete,
  Checkbox,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Pagination, // pagination mobile
  TablePagination, // pagination desktop
  Stack,
  Chip,
  alpha,
} from '@mui/material';

import { Link } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

import VideocamIcon from '@mui/icons-material/Videocam';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import { canModifyRecord } from '../utils/permissions';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';

import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../api/axiosConfig';
import { useClubTheme } from '../contexts/ClubThemeContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import RechercheInformeContext from './RechercheInformeContext';
import { getPlayerPhotoUrl, getUserAvatarUrl, getMediaBaseUrl } from '../utils/mediaUrl';
import AgeAtInformeChip from '../components/AgeAtInformeChip';
import InformeClipsSection from '../components/InformeClipsSection';
import DefensiveVersatilityChart from '../components/DefensiveVersatility/DefensiveVersatilityChart';
import DefensiveVersatilityEditor from '../components/DefensiveVersatility/DefensiveVersatilityEditor';
import { normalizeVersatility, hasAnyValue } from '../utils/versatility';
import { usePositions } from '../utils/positions';

// === Configuration pour l'Autocomplete "virtualisé" ===
const LISTBOX_PADDING = 8;
function renderRow(props) {
  const { data, index, style } = props;
  const item = data[index];
  return React.cloneElement(item, {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return (
    <div
      ref={ref}
      {...props}
      {...outerProps}
      style={{
        ...props.style,
        overflowX: 'hidden',
        overflowY: 'scroll',
        maxWidth: '100%',
      }}
    />
  );
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const getChildSize = () => itemSize;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={getChildSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});


// === Fonctions utilitaires ===
const getInitials = (nom, prenom) => {
  const firstInitial = nom ? nom[0] : '';
  const secondInitial = prenom ? prenom[0] : '';
  return (firstInitial + secondInitial).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return '#757575';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
};


/**
 * Calcule l'âge en décimal (ex: 15.4) en se basant sur la fecha de l'informe et la date de naissance du joueur.
 */
const getDecimalAge = (fecha, dob) => {
  if (!fecha || !dob) return '';
  const dateInforme = new Date(fecha);
  const dateNaissance = new Date(dob);

  const diffMs = dateInforme - dateNaissance;
  if (diffMs < 0) return '';
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const diffYears = diffDays / 365;
  return diffYears.toFixed(1);
};

/**
 * Extrait l'abréviation entre parenthèses dans la nationalité (ex: "Afgano (AFG)" => "AFG").
 * Gère l'ancien format (avec parenthèses) et le nouveau format (clé de traduction).
 */
const getShortNationality = (nat, t) => {
  if (!nat) return '';
  // 1. Vérifier si la valeur contient déjà des parenthèses (ancien format)
  const directMatch = nat.match(/\((.*?)\)/);
  if (directMatch) return directMatch[1];
  // 2. Sinon, essayer de traduire la clé (nouveau format)
  const translatedNationality = t ? t(`createPlayer.nationalities.${nat}`, { defaultValue: '' }) : '';
  if (translatedNationality) {
    const translatedMatch = translatedNationality.match(/\((.*?)\)/);
    if (translatedMatch) return translatedMatch[1];
  }
  // 3. Retourner la valeur brute si rien ne fonctionne
  return nat;
};

// Ignore les valeurs "null"/"undefined" stockées en texte dans la BDD et joint les ligues restantes
const formatLeagues = (...leagues) =>
  leagues.filter((l) => l && l !== 'null' && l !== 'undefined').join(' - ');

// Helper to get media URL for an informe
const getInformeMediaUrl = (mediaFilename) => {
  return `${getMediaBaseUrl()}/mediarapport/${mediaFilename}?${new Date().getTime()}`;
};

// Helper to check if media is an image
const isImageMedia = (filename) => {
  if (!filename) return false;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

// Helper to check if media is a PDF
const isPdfMedia = (filename) => {
  if (!filename) return false;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

// === Composant MobileInformeCard ===
const MobileInformeCard = ({ informe, onEdit, onDelete, open, onToggle, user, t, translateFromDB, onOpenStats }) => {
  const theme = useTheme();
  const decimalAge = getDecimalAge(informe.fecha, informe.player_date_naissance);
  const shortNat = getShortNationality(informe.player_nationalite, t);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={
          <Avatar src={getPlayerPhotoUrl(informe.player_id)} alt={informe.player_nom} />
        }
        title={
          user?.role?.toLowerCase() === 'entrenador' || user?.role?.toLowerCase() === 'pdp'
            ? (
              <Typography>{informe.player_nom}</Typography>
            ) : (
              <Link
                to={`/players/${informe.player_id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {informe.player_nom}
              </Link>
            )
        }
        subheader={new Date(informe.fecha).toLocaleDateString()}
        action={
          <IconButton onClick={() => onToggle(informe.id)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={getUserAvatarUrl(informe.usuario_id)} sx={{ width: 24, height: 24 }} />
            <Typography variant="body2" color="text.secondary">
              {informe.usuario_prenom} {informe.usuario_nom}
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('rechercheInforme.position')}:</strong> {translateFromDB(informe.player_poste)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1">
              <strong>{t('rechercheInforme.age')}:</strong> {informe.player_annee_naissance}
            </Typography>
            <AgeAtInformeChip
              dateNaissance={informe.player_date_naissance}
              informeDate={informe.fecha}
            />
          </Box>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('rechercheInforme.nationality')}:</strong> {shortNat}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" component="div" gutterBottom>
              <strong>{t('rechercheInforme.team')}:</strong>
            </Typography>
            {!!informe.player_is_free_agent && (
              <Chip
                label={t('createPlayer.fields.freeAgentBadge')}
                size="small"
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  height: '22px',
                  mb: 0.5,
                }}
              />
            )}
            {informe.player_competitions_detail && informe.player_competitions_detail.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {[...new Set(informe.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                  const equipeData = informe.player_competitions_detail.find(c => c.equipe_nom === equipe);
                  const competitions = informe.player_competitions_detail
                    .filter(c => c.equipe_nom === equipe)
                    .filter((value, index, self) =>
                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                    );
                  const mediaUrl = getMediaBaseUrl();
                  const timestamp = Date.now();

                  return (
                    <Box key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          component="img"
                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                          alt={equipe}
                          sx={{ width: 20, height: 20, objectFit: 'contain', borderRadius: '2px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{equipe}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3.5, mt: 0.3 }}>
                        {competitions.map((comp, compIdx) => (
                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Box
                              component="img"
                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                              alt={comp.competition_nom}
                              sx={{ width: 14, height: 14, objectFit: 'contain', borderRadius: '2px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <Typography variant="caption" color="text.secondary">{comp.competition_nom}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box>
                <Typography variant="body2">{informe.equipo && informe.equipo !== 'null' ? informe.equipo : 'N/A'}</Typography>
                {formatLeagues(informe.player_league_1, informe.player_league_2) && (
                  <Typography variant="caption" color="text.secondary">
                    {formatLeagues(informe.player_league_1, informe.player_league_2)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('rechercheInforme.event')}:</strong> {informe.evento}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('rechercheInforme.scouting')}:</strong>{' '}
            {informe.visu === 'video' ? 'Video' : informe.visu === 'live' ? 'Live' : ''}
          </Typography>

          {/* Nivel, Projection, FIT, Stats */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
            {informe.nivel && (
              <Chip
                icon={<SignalCellularAltIcon sx={{ fontSize: 14 }} />}
                label={t(`createPlayer.levels.${informe.nivel}`, informe.nivel)}
                size="small"
                sx={{ backgroundColor: '#e8f5e9', fontWeight: 600 }}
              />
            )}
            {informe.projection && (
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                label={t(`createPlayer.levels.${informe.projection}`, informe.projection)}
                size="small"
                sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}
              />
            )}
            {(informe.fit_estilo !== null || informe.fit_fisico !== null || informe.fit_actitud !== null || informe.fit_mejora !== null || informe.fit_contexto !== null) && (
              (() => {
                const fitItems = [
                  { val: informe.fit_estilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                  { val: informe.fit_fisico, label: t('playerCardCreator.informeForm.fitFisico') },
                  { val: informe.fit_actitud, label: t('playerCardCreator.informeForm.fitActitud') },
                  { val: informe.fit_mejora, label: t('playerCardCreator.informeForm.fitMejora') },
                  { val: informe.fit_contexto, label: t('playerCardCreator.informeForm.fitContexto') }
                ].filter(f => f.val !== null && f.val !== undefined);
                const fitYes = fitItems.filter(f => f.val).length;
                const fitTotal = fitItems.length;
                const fitPercent = Math.round((fitYes / fitTotal) * 100);
                const fitColor = fitPercent >= 80 ? '#4caf50' : fitPercent >= 50 ? '#ff9800' : '#f44336';
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        FIT:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
                        <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', overflow: 'hidden', minWidth: 40 }}>
                          <Box sx={{ width: `${fitPercent}%`, height: '100%', borderRadius: 3, bgcolor: fitColor, transition: 'width 0.3s' }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: fitColor, fontSize: '0.7rem' }}>
                          {fitYes}/{fitTotal}
                        </Typography>
                      </Box>
                    </Box>
                    {fitItems.map((f, i) => (
                      <Chip
                        key={i}
                        label={`${f.label} ${f.val ? '✓' : '✗'}`}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          backgroundColor: f.val ? '#e8f5e9' : '#ffebee',
                          color: f.val ? '#2e7d32' : '#c62828',
                        }}
                      />
                    ))}
                  </>
                );
              })()
            )}
            {informe.stats_url && (
              <Chip
                icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                label={t('playerCardCreator.informeForm.statsUrl')}
                size="small"
                onClick={() => onOpenStats && onOpenStats(informe.stats_url)}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#e3f2fd', color: '#1565c0' }}
              />
            )}
          </Box>

          {hasAnyValue(normalizeVersatility(informe.defensive_versatility)) && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <DefensiveVersatilityChart value={informe.defensive_versatility} compact showLegend showGroups={false} showTitle />
            </Box>
          )}

          <Typography
            variant="body2"
            component="div"
            sx={{
              backgroundColor: '#FFECB3',
              p: 2,
              borderRadius: 1,
              mt: 1,
              mb: 2,
            }}
            dangerouslySetInnerHTML={{ __html: informe.detalles }}
          />

          {/* Media attachment display */}
          {informe.media_filename && (
            <Box sx={{ mt: 2 }}>
              {isImageMedia(informe.media_filename) ? (
                <Box
                  component="img"
                  src={getInformeMediaUrl(informe.media_filename)}
                  alt="Media"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(getInformeMediaUrl(informe.media_filename), '_blank')}
                />
              ) : isPdfMedia(informe.media_filename) ? (
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  endIcon={<OpenInNewIcon />}
                  onClick={() => window.open(getInformeMediaUrl(informe.media_filename), '_blank')}
                  sx={{
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: alpha('#f44336', 0.05)
                    }
                  }}
                >
                  {t('playerCardCreator.informeTab.viewPdf')}
                </Button>
              ) : (
                <Chip
                  icon={<AttachFileIcon />}
                  label={informe.media_filename}
                  onClick={() => window.open(getInformeMediaUrl(informe.media_filename), '_blank')}
                  sx={{ cursor: 'pointer' }}
                />
              )}
            </Box>
          )}

          {/* Clips vidéo liés à l'informe */}
          <InformeClipsSection informeId={informe.id} expanded={open} />
        </CardContent>
        {canModifyRecord(user, informe.usuario_id) && (
          <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(informe)}>
              {t('rechercheInforme.edit')}
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(informe)}
            >
              {t('rechercheInforme.delete')}
            </Button>
          </CardActions>
        )}
      </Collapse>
    </Card>
  );
};

// === Sélecteur visuel Pro / Cantera / Tous ===
const PlayerTypeToggle = ({ value, onChange, t }) => {
  const handleChange = (_event, newValue) => {
    if (newValue !== null) onChange(newValue);
  };
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      fullWidth
      sx={{
        height: 56,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          gap: 0.75,
          borderColor: 'rgba(0,0,0,0.23)',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-all': {
          backgroundColor: alpha('#607d8b', 0.15),
          color: '#37474f',
          borderColor: '#607d8b',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-pro': {
          backgroundColor: alpha('#ff9800', 0.18),
          color: '#e65100',
          borderColor: '#ff9800',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-cantera': {
          backgroundColor: alpha('#2e7d32', 0.18),
          color: '#1b5e20',
          borderColor: '#2e7d32',
        },
      }}
    >
      <ToggleButton value="all" className="toggle-all">
        <GroupsIcon fontSize="small" />
        {t('rechercheInforme.all')}
      </ToggleButton>
      <ToggleButton value="pro" className="toggle-pro">
        <SportsBasketballIcon fontSize="small" />
        {t('rechercheInforme.pro')}
      </ToggleButton>
      <ToggleButton value="cantera" className="toggle-cantera">
        <SchoolIcon fontSize="small" />
        {t('rechercheInforme.cantera')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

// === Composant tiroir de filtres (mobile) ===
const MobileFilterDrawer = ({
  open,
  onClose,
  players,
  users,
  eventos,
  selectedPlayer,
  selectedUser,
  onPlayerChange,
  onUserChange,
  selectedEventos,
  onEventosChange,
  selectedPlayerType,
  onPlayerTypeChange,
  isSuperAdmin,
  t,
}) => {
  const theme = useTheme();

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{t('rechercheInforme.filters')}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                options={players}
                getOptionLabel={(player) => player.nom}
                value={selectedPlayer}
                onChange={onPlayerChange}
                ListboxComponent={ListboxComponent}
                renderInput={(params) => (
                  <TextField {...params} label={t('rechercheInforme.selectPlayer')} variant="outlined" />
                )}
                renderOption={(props, player) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={getPlayerPhotoUrl(player.id)}
                      alt={player.nom}
                      sx={{ mr: 1, width: 32, height: 32 }}
                    />
                    {player.nom}
                  </Box>
                )}
                clearOnEscape
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.prenom} ${user.nom}`}
                value={selectedUser}
                onChange={onUserChange}
                ListboxComponent={ListboxComponent}
                renderInput={(params) => (
                  <TextField {...params} label={t('rechercheInforme.selectUser')} variant="outlined" />
                )}
                renderOption={(props, user) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={getUserAvatarUrl(user.id)}
                      alt={user.nom}
                      sx={{ mr: 1, width: 32, height: 32 }}
                    />
                    {user.prenom} {user.nom}
                  </Box>
                )}
                clearOnEscape
              />
            </FormControl>
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12}>
              <PlayerTypeToggle
                value={selectedPlayerType}
                onChange={onPlayerTypeChange}
                t={t}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={eventos}
              value={selectedEventos}
              onChange={onEventosChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('rechercheInforme.selectEvent')}
                  placeholder={t('rechercheInforme.selectOrTypeEvent')}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option}
                </li>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  );
};

// === Composant principal RechercheInforme ===
const RechercheInforme = ({ onDataLoaded }) => {
  const { t } = useTranslation();
  const { clubName } = useClubTheme();
  const { translateFromDB } = usePositions(t);
  const {
    // Filtres
    selectedPlayer,
    setSelectedPlayer,
    selectedUser,
    setSelectedUser,
    selectedEventos,
    setSelectedEventos,
    selectedPlayerType,
    setSelectedPlayerType,
    // gestion des rows "ouvertes" (Collapse)
    openRows,
    toggleRow,
  } = useContext(RechercheInformeContext);

  const [allInformeData, setAllInformeData] = useState([]);
  const [informeData, setInformeData] = useState([]);

  // État pour gérer l'édition
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentInforme, setCurrentInforme] = useState(null);
  const [editedDetalles, setEditedDetalles] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedEvento, setEditedEvento] = useState('');
  const [editedEquipo, setEditedEquipo] = useState('');
  const [editedVisu, setEditedVisu] = useState('');
  const [editedMediaFile, setEditedMediaFile] = useState(null);
  const [editedMediaPreviewUrl, setEditedMediaPreviewUrl] = useState(null);
  const [removeExistingMedia, setRemoveExistingMedia] = useState(false);
  const [editedNivel, setEditedNivel] = useState('');
  const [editedProjection, setEditedProjection] = useState('');
  const [editedFitEstilo, setEditedFitEstilo] = useState(null);
  const [editedFitFisico, setEditedFitFisico] = useState(null);
  const [editedFitActitud, setEditedFitActitud] = useState(null);
  const [editedFitMejora, setEditedFitMejora] = useState(null);
  const [editedFitContexto, setEditedFitContexto] = useState(null);
  const [editedStatsUrl, setEditedStatsUrl] = useState('');
  const [editedDefensiveVersatility, setEditedDefensiveVersatility] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsDialogUrl, setStatsDialogUrl] = useState('');
  const [iframeError, setIframeError] = useState(false);
  const editFileInputRef = useRef(null);

  // État pour la suppression
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [informeToDelete, setInformeToDelete] = useState(null);

  // Datas "brutes"
  const [eventos, setEventos] = useState([]);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [existingEquipos, setExistingEquipos] = useState([]);

  // L'utilisateur "complet"
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        // On récupère l'utilisateur localStorage
        const localStoredUser = JSON.parse(localStorage.getItem('user'));

        // On charge en // :
        // - tous les informes
        // - la liste de tous les joueurs
        // - la liste de tous les utilisateurs (pour retrouver l'user complet)
        // - les teams (optionnel)
        // - la liste des eventos
        const [
          resInforme,
          resPlayers,
          resUsers,
          resTeams,
          resEventos,
          resEquipos
        ] = await Promise.all([
          api.get('/api/informe/all'),
          api.get('/api/players/select'),
          api.get('/api/users'),
          api.get('/api/players/teams'),
          api.get('/api/informe/eventos'),
          api.get('/api/informe/equipos'),
        ]);

        let informes = resInforme.data || [];

        // Convertir les données "users", "players" etc.
        const allPlayers = Array.isArray(resPlayers.data)
          ? resPlayers.data
          : resPlayers.data.players || [];
        const allUsers = Array.isArray(resUsers.data)
          ? resUsers.data
          : resUsers.data.users || [];

        const teamsResponse = resTeams.data;
        const allTeams = teamsResponse.teams || [];

        setPlayers(allPlayers);
        setUsers(allUsers);
        setTeams(allTeams);
        setEventos(resEventos.data);
        setExistingEquipos(resEquipos.data || []);

        // On essaye de retrouver l'user "complet" (avec ses groupes) dans allUsers
        let finalUser = null;
        if (localStoredUser) {
          finalUser = allUsers.find((u) => u.id === localStoredUser.id) || localStoredUser;
        }
        setUser(finalUser);

        // === Filtrage selon le rôle ===
        if (finalUser) {
          const roleLower = finalUser.role?.toLowerCase();
          if (roleLower === 'entrenador' || roleLower === 'pdp') {
            // On prend la liste des groupes de l'utilisateur
            const userGroups = finalUser.groupes || [];
            informes = informes.filter((inf) => {
              // Condition A : L'informe est créé par l'utilisateur
              const createdByUser = inf.usuario_id === finalUser.id;
              // Condition B : Intersection de groupes
              const playerGroups = inf.player_groupes || [];
              const hasIntersection = playerGroups.some((pg) =>
                userGroups.some((ug) => ug.id === pg.id)
              );
              return createdByUser || hasIntersection;
            });
          } else if (roleLower === 'admin pro') {
            // Voir tous les informes "pro" ou créés par lui 
            informes = informes.filter(
              (inf) =>
                inf.player_type?.toLowerCase() === 'pro'
                || inf.usuario_id === finalUser.id
            );
          } else if (roleLower === 'admin cantera') {
            // Voir tous les informes "cantera" ou créés par lui
            informes = informes.filter(
              (inf) =>
                inf.player_type?.toLowerCase() === 'cantera'
                || inf.usuario_id === finalUser.id
            );
          } else if (roleLower === 'scout interne' || roleLower === 'scout externe') {
            // Les scouts ne voient que leurs propres rapports
            informes = informes.filter((inf) => inf.usuario_id === finalUser.id);
          } else if (roleLower === 'scout interne cantera' || roleLower === 'scout externe cantera') {
            // Les scouts cantera ne voient que leurs propres rapports sur joueurs cantera
            informes = informes.filter((inf) => inf.player_type?.toLowerCase() === 'cantera' && inf.usuario_id === finalUser.id);
          }
        }

        // On stocke tout ça
        setAllInformeData(informes);
        setInformeData(informes);

        if (onDataLoaded) {
          onDataLoaded(informes.length > 0);
        }

      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [backendUrl, section, onDataLoaded]);

  // === Filtrage "local" par les champs de recherche (joueur, user, etc.) ===
  useEffect(() => {
    let filteredData = allInformeData;

    if (selectedPlayer) {
      filteredData = filteredData.filter((inf) => inf.player_id === selectedPlayer.id);
    }
    if (selectedUser) {
      filteredData = filteredData.filter((inf) => inf.usuario_id === selectedUser.id);
    }
    if (selectedEventos.length > 0) {
      filteredData = filteredData.filter((inf) => selectedEventos.includes(inf.evento));
    }
    if (selectedPlayerType && selectedPlayerType !== 'all') {
      filteredData = filteredData.filter(
        (inf) => inf.player_type?.toLowerCase() === selectedPlayerType
      );
    }

    setInformeData(filteredData);
    setPage(0);
  }, [
    allInformeData,
    selectedPlayer,
    selectedUser,
    selectedEventos,
    selectedPlayerType,
  ]);

  // Generate preview URL for edited media file
  useEffect(() => {
    if (editedMediaFile && editedMediaFile.type?.startsWith('image/')) {
      const url = URL.createObjectURL(editedMediaFile);
      setEditedMediaPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setEditedMediaPreviewUrl(null);
    }
  }, [editedMediaFile]);

  // Paste handler for screenshots in edit dialog
  useEffect(() => {
    if (!openEditDialog) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Rename file with timestamp
            const extension = file.type.split('/')[1] || 'png';
            const newFile = new File([file], `screenshot_${Date.now()}.${extension}`, {
              type: file.type
            });
            setEditedMediaFile(newFile);
            setRemoveExistingMedia(false);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [openEditDialog]);

  // === Pagination ===
  const totalResults = informeData.length;
  const paginatedData = React.useMemo(() => {
    if (rowsPerPage <= 0) {
      return informeData;
    }
    return informeData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [informeData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    const newVal = parseInt(event.target.value, 10);
    setRowsPerPage(newVal);
    setPage(0);
  };
  const handleChangeMobilePage = (event, newValue) => {
    setPage(newValue - 1);
  };

  // === Toggle (ouvrir/fermer le Collapse) ===
  const handleToggleRow = (rowId) => {
    toggleRow(rowId);
  };

  // === Gestion d'édition ===
  const handleEditClick = (informe) => {
    setCurrentInforme(informe);
    setEditedDetalles(informe.detalles || '');

    // Corriger la date en ISO pour le <TextField type="date">
    if (informe.fecha) {
      let dateObj = new Date(informe.fecha);
      dateObj = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
      const isoDate = dateObj.toISOString().split('T')[0];
      setEditedDate(isoDate);
    } else {
      setEditedDate('');
    }
    setEditedEvento(informe.evento || '');
    setEditedEquipo(informe.equipo || '');
    setEditedVisu(informe.visu || '');
    // Reset media states
    setEditedMediaFile(null);
    setEditedMediaPreviewUrl(null);
    setRemoveExistingMedia(false);
    // Nivel, Projection, FIT, Stats
    setEditedNivel(informe.nivel || '');
    setEditedProjection(informe.projection || '');
    setEditedFitEstilo(informe.fit_estilo !== null && informe.fit_estilo !== undefined ? Boolean(informe.fit_estilo) : null);
    setEditedFitFisico(informe.fit_fisico !== null && informe.fit_fisico !== undefined ? Boolean(informe.fit_fisico) : null);
    setEditedFitActitud(informe.fit_actitud !== null && informe.fit_actitud !== undefined ? Boolean(informe.fit_actitud) : null);
    setEditedFitMejora(informe.fit_mejora !== null && informe.fit_mejora !== undefined ? Boolean(informe.fit_mejora) : null);
    setEditedFitContexto(informe.fit_contexto !== null && informe.fit_contexto !== undefined ? Boolean(informe.fit_contexto) : null);
    setEditedStatsUrl(informe.stats_url || '');
    setEditedDefensiveVersatility(normalizeVersatility(informe.defensive_versatility));

    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setCurrentInforme(null);
    setEditedMediaFile(null);
    setEditedMediaPreviewUrl(null);
    setRemoveExistingMedia(false);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleEditSave = async () => {
    try {
      // Nettoyage du contenu Quill
      const transformedDetalles = editedDetalles
        .replace(/<p><br><\/p>/g, '<br>')
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '<br>')
        .replace(/<br><br>/g, '<br>');

      let response;
      let newMediaFilename = currentInforme.media_filename;

      // Use FormData if there's a new media file or if we need to remove existing media
      if (editedMediaFile || removeExistingMedia) {
        const formData = new FormData();
        formData.append('detalles', transformedDetalles);
        formData.append('fecha', editedDate);
        formData.append('evento', editedEvento);
        formData.append('equipo', editedEquipo);
        formData.append('visu', editedVisu);
        formData.append('nivel', editedNivel);
        formData.append('projection', editedProjection);
        if (editedFitEstilo !== null) formData.append('fit_estilo', editedFitEstilo ? '1' : '0');
        else formData.append('fit_estilo', '');
        if (editedFitFisico !== null) formData.append('fit_fisico', editedFitFisico ? '1' : '0');
        else formData.append('fit_fisico', '');
        if (editedFitActitud !== null) formData.append('fit_actitud', editedFitActitud ? '1' : '0');
        else formData.append('fit_actitud', '');
        if (editedFitMejora !== null) formData.append('fit_mejora', editedFitMejora ? '1' : '0');
        else formData.append('fit_mejora', '');
        if (editedFitContexto !== null) formData.append('fit_contexto', editedFitContexto ? '1' : '0');
        else formData.append('fit_contexto', '');
        formData.append('stats_url', editedStatsUrl || '');
        formData.append('defensive_versatility', editedDefensiveVersatility ? JSON.stringify(editedDefensiveVersatility) : '');

        if (editedMediaFile) {
          formData.append('media', editedMediaFile);
        }
        if (removeExistingMedia) {
          formData.append('removeMedia', 'true');
        }

        response = await api.put(
          `/api/informe/${currentInforme.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        // Update media filename from response
        if (response.data.mediaFilename !== undefined) {
          newMediaFilename = response.data.mediaFilename;
        } else if (removeExistingMedia && !editedMediaFile) {
          newMediaFilename = null;
        }
      } else {
        const updateData = {
          detalles: transformedDetalles,
          fecha: editedDate,
          evento: editedEvento,
          equipo: editedEquipo,
          visu: editedVisu,
          nivel: editedNivel,
          projection: editedProjection,
          fit_estilo: editedFitEstilo !== null ? (editedFitEstilo ? 1 : 0) : null,
          fit_fisico: editedFitFisico !== null ? (editedFitFisico ? 1 : 0) : null,
          fit_actitud: editedFitActitud !== null ? (editedFitActitud ? 1 : 0) : null,
          fit_mejora: editedFitMejora !== null ? (editedFitMejora ? 1 : 0) : null,
          fit_contexto: editedFitContexto !== null ? (editedFitContexto ? 1 : 0) : null,
          stats_url: editedStatsUrl || null,
          defensive_versatility: editedDefensiveVersatility || null,
        };

        response = await api.put(`/api/informe/${currentInforme.id}`, updateData);
      }

      // Mettre à jour la liste en mémoire
      setAllInformeData((prev) =>
        prev.map((item) =>
          item.id === currentInforme.id
            ? {
                ...item,
                detalles: transformedDetalles,
                fecha: editedDate,
                evento: editedEvento,
                equipo: editedEquipo,
                visu: editedVisu,
                nivel: editedNivel,
                projection: editedProjection,
                media_filename: newMediaFilename,
                fit_estilo: editedFitEstilo !== null ? (editedFitEstilo ? 1 : 0) : null,
                fit_fisico: editedFitFisico !== null ? (editedFitFisico ? 1 : 0) : null,
                fit_actitud: editedFitActitud !== null ? (editedFitActitud ? 1 : 0) : null,
                fit_mejora: editedFitMejora !== null ? (editedFitMejora ? 1 : 0) : null,
                fit_contexto: editedFitContexto !== null ? (editedFitContexto ? 1 : 0) : null,
                stats_url: editedStatsUrl || null,
                defensive_versatility: editedDefensiveVersatility || null,
              }
            : item
        )
      );

      // Eventuellement, recharger la liste des "eventos"
      try {
        const timestamp = new Date().getTime();
        const resEventos = await api.get('/api/informe/eventos', {
          params: { t: timestamp },
        });
        setEventos(resEventos.data);
      } catch (err) {
        console.error('Error reloading eventos:', err);
      }

      handleCloseEditDialog();
      alert(t('rechercheInforme.reportUpdatedSuccess'));
    } catch (error) {
      console.error('Error al actualizar el informe:', error);
      alert(t('rechercheInforme.reportUpdateError'));
    }
  };

  // === Suppression d'un informe ===
  const handleDeleteClick = (informe) => {
    setInformeToDelete(informe);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/informe/${informeToDelete.id}`);
      // Mise à jour du state
      setAllInformeData((prev) => prev.filter((item) => item.id !== informeToDelete.id));
      setOpenDeleteDialog(false);
      alert(t('rechercheInforme.reportDeletedSuccess'));
    } catch (error) {
      console.error('Error al eliminar el informe:', error);
      alert(t('rechercheInforme.reportDeleteError'));
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setInformeToDelete(null);
  };

  // === Rendu principal ===
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* ----------- VUE SMARTPHONE ----------- */}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {totalResults === 1 ? t('rechercheInforme.reportFound') : t('rechercheInforme.reportsFound')}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            {paginatedData.map((informe) => (
              <MobileInformeCard
                key={informe.id}
                informe={informe}
                user={user}
                open={openRows[informe.id] || false}
                onToggle={handleToggleRow}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
                translateFromDB={translateFromDB}
                onOpenStats={(url) => { setStatsDialogUrl(url); setIframeError(false); setStatsDialogOpen(true); }}
              />
            ))}
          </Box>

          {/* Pagination mobile */}
          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1} // base 1
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}

          {/* Bouton pour ouvrir le tiroir de filtres (mobile) */}
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: theme.zIndex.fab,
            }}
            onClick={() => setOpenFilterDrawer(true)}
          >
            <FilterListIcon />
          </Fab>

          {/* Tiroir de filtres mobile */}
          <MobileFilterDrawer
            open={openFilterDrawer}
            onClose={() => setOpenFilterDrawer(false)}
            players={players}
            users={users}
            eventos={eventos}
            selectedPlayer={selectedPlayer}
            selectedUser={selectedUser}
            onPlayerChange={(_, newValue) => setSelectedPlayer(newValue)}
            onUserChange={(_, newValue) => setSelectedUser(newValue)}
            selectedEventos={selectedEventos}
            onEventosChange={(_, newValues) => setSelectedEventos(newValues)}
            selectedPlayerType={selectedPlayerType}
            onPlayerTypeChange={(val) => setSelectedPlayerType(val)}
            isSuperAdmin={user?.role?.toLowerCase() === 'super admin'}
            t={t}
          />
        </>
      ) : (
        <>
          {/* ----------- VUE DESKTOP ----------- */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <Autocomplete
                  options={players}
                  getOptionLabel={(player) => player.nom}
                  value={selectedPlayer}
                  onChange={(_, newValue) => setSelectedPlayer(newValue)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => (
                    <TextField {...params} label={t('rechercheInforme.selectPlayer')} variant="outlined" />
                  )}
                  renderOption={(props, player) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getPlayerPhotoUrl(player.id)}
                        alt={player.nom}
                        sx={{ mr: 1 }}
                      />
                      {player.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <Autocomplete
                  options={users}
                  getOptionLabel={(u) => `${u.prenom} ${u.nom}`}
                  value={selectedUser}
                  onChange={(_, newValue) => setSelectedUser(newValue)}
                  ListboxComponent={ListboxComponent}
                  renderInput={(params) => (
                    <TextField {...params} label={t('rechercheInforme.selectUser')} variant="outlined" />
                  )}
                  renderOption={(props, u) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getUserAvatarUrl(u.id)}
                        alt={u.nom}
                        sx={{ width: 30, height: 30, mr: 1 }}
                      />
                      {u.prenom} {u.nom}
                    </Box>
                  )}
                  clearOnEscape
                />
              </FormControl>
            </Grid>

            {user?.role?.toLowerCase() === 'super admin' && (
              <Grid item xs={12} sm={3}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={user?.role?.toLowerCase() === 'super admin' ? 5 : 8}>
              <Autocomplete
                multiple
                freeSolo
                options={eventos}
                value={selectedEventos}
                onChange={(_, newValues) => setSelectedEventos(newValues)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('rechercheInforme.selectEvent')}
                    variant="outlined"
                    placeholder={t('rechercheInforme.selectOrTypeEvent')}
                    fullWidth
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </li>
                )}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {totalResults === 1 ? t('rechercheInforme.reportFound') : t('rechercheInforme.reportsFound')}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t('rechercheInforme.creator')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.date')}</TableCell>
                  <TableCell>{t('rechercheInforme.player')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.position')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.age')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.nationality')}</TableCell>
                  <TableCell>{t('rechercheInforme.team')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.event')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.scouting')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.details')}</TableCell>
                  <TableCell align="center">{t('rechercheInforme.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  const decimalAge = getDecimalAge(row.fecha, row.player_date_naissance);
                  const shortNat = getShortNationality(row.player_nationalite, t);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={(e) => {
                          // Ne pas toggler si on clique sur un lien, bouton ou action
                          if (
                            e.target.closest('a') ||
                            e.target.closest('button') ||
                            e.target.closest('[role="button"]')
                          ) {
                            return;
                          }
                          toggleRow(row.id);
                        }}
                      >
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: 'white',
                                    '& .MuiTooltip-arrow': { color: 'white' },
                                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                  },
                                },
                              }}
                              title={
                                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar
                                    src={getUserAvatarUrl(row.usuario_id)}
                                    sx={{ width: 50, height: 50 }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 'bold', color: 'black' }}
                                    >
                                      {row.usuario_prenom} {row.usuario_nom}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'rgba(0,0,0,0.7)' }}
                                    >
                                      {row.usuario_nombre}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            >
                              <Avatar
                                sx={{
                                  bgcolor: getAvatarColor(row.usuario_nom + row.usuario_prenom),
                                }}
                              >
                                {getInitials(row.usuario_prenom, row.usuario_nom)}
                              </Avatar>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {new Date(row.fecha).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              src={getPlayerPhotoUrl(row.player_id)}
                              alt={row.player_nom}
                              sx={{ mr: 1 }}
                            />
                            {user?.role?.toLowerCase() === 'entrenador' ||
                            user?.role?.toLowerCase() === 'pdp' ? (
                              <Typography variant="body2">{row.player_nom}</Typography>
                            ) : (
                              <Typography variant="body2">
                                <Link
                                  to={`/players/${row.player_id}`}
                                  style={{ textDecoration: 'none', color: 'inherit' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row.player_nom}
                                </Link>
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{translateFromDB(row.player_poste)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <span>{row.player_annee_naissance}</span>
                            <AgeAtInformeChip
                              dateNaissance={row.player_date_naissance}
                              informeDate={row.fecha}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">{shortNat}</TableCell>
                        <TableCell>
                          <Box>
                            {!!row.player_is_free_agent && (
                              <Chip
                                label={t('createPlayer.fields.freeAgentBadge')}
                                size="small"
                                sx={{
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '10px',
                                  height: '20px',
                                  mb: 0.5,
                                }}
                              />
                            )}
                            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                            {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                              <>
                                {/* Grouper par équipe */}
                                {[...new Set(row.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                  // Récupérer l'ID de l'équipe et les compétitions associées
                                  const equipeData = row.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                  const competitions = row.player_competitions_detail
                                    .filter(c => c.equipe_nom === equipe)
                                    .filter((value, index, self) =>
                                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                                    );
                                  const mediaUrl = getMediaBaseUrl();
                                  const timestamp = Date.now();

                                  return (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {/* Logo de l'équipe */}
                                        <Box
                                          component="img"
                                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                          alt={equipe}
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            objectFit: 'contain',
                                            borderRadius: '2px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                      </Box>
                                      {/* Logos et noms des compétitions */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                        {competitions.map((comp, compIdx) => (
                                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                            <Box
                                              component="img"
                                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                              alt={comp.competition_nom}
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                objectFit: 'contain',
                                                borderRadius: '2px'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                            <span style={{ fontSize: '10px' }}>{comp.competition_nom}</span>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </>
                            ) : (
                              // Sinon afficher l'équipe saisie dans l'informe (et optionnellement les ligues)
                              <Box>
                                {row.equipo && row.equipo !== 'null' && (
                                  <div style={{ fontSize: '12px' }}>{row.equipo}</div>
                                )}
                                {formatLeagues(row.player_league_1, row.player_league_2) && (
                                  <div style={{ fontSize: '10px', color: '#666' }}>
                                    {formatLeagues(row.player_league_1, row.player_league_2)}
                                  </div>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{row.evento}</TableCell>
                        <TableCell align="center">
                          {row.visu === 'video' ? (
                            <Tooltip title="Video">
                              <VideocamIcon />
                            </Tooltip>
                          ) : row.visu === 'live' ? (
                            <Tooltip title="Live">
                              <PersonPinCircleIcon />
                            </Tooltip>
                          ) : (
                            ''
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRow(row.id);
                            }}
                          >
                            {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {canModifyRecord(user, row.usuario_id) && (
                            <>
                              <IconButton onClick={() => handleEditClick(row)} color="primary">
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => handleDeleteClick(row)} sx={{ color: 'red' }}>
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Ligne de détails (Collapse) */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                          <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                p: 3,
                                backgroundColor: theme.palette.mode === 'light'
                                  ? theme.palette.grey[50]
                                  : alpha(theme.palette.background.paper, 0.5),
                                borderTop: `1px solid ${theme.palette.divider}`,
                                '& p': { marginBottom: 1.5, lineHeight: 1.7 }
                              }}
                            >
                              {/* Bloc 1 — Nivel, Projection, FIT, Stats */}
                              {(row.nivel || row.projection || row.stats_url ||
                                row.fit_estilo !== null || row.fit_fisico !== null ||
                                row.fit_actitud !== null || row.fit_mejora !== null ||
                                row.fit_contexto !== null) && (
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  alignItems: 'center'
                                }}
                              >
                                {row.nivel && (
                                  <Chip
                                    icon={<SignalCellularAltIcon sx={{ fontSize: 14 }} />}
                                    label={t(`createPlayer.levels.${row.nivel}`, row.nivel)}
                                    size="small"
                                    sx={{ backgroundColor: '#e8f5e9', fontWeight: 600 }}
                                  />
                                )}
                                {row.projection && (
                                  <Chip
                                    icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                                    label={t(`createPlayer.levels.${row.projection}`, row.projection)}
                                    size="small"
                                    sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}
                                  />
                                )}
                                {(row.fit_estilo !== null || row.fit_fisico !== null || row.fit_actitud !== null || row.fit_mejora !== null || row.fit_contexto !== null) && (
                                  (() => {
                                    const fitItems = [
                                      { val: row.fit_estilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                                      { val: row.fit_fisico, label: t('playerCardCreator.informeForm.fitFisico') },
                                      { val: row.fit_actitud, label: t('playerCardCreator.informeForm.fitActitud') },
                                      { val: row.fit_mejora, label: t('playerCardCreator.informeForm.fitMejora') },
                                      { val: row.fit_contexto, label: t('playerCardCreator.informeForm.fitContexto') }
                                    ].filter(f => f.val !== null && f.val !== undefined);
                                    const fitYes = fitItems.filter(f => f.val).length;
                                    const fitTotal = fitItems.length;
                                    const fitPercent = Math.round((fitYes / fitTotal) * 100);
                                    const fitColor = fitPercent >= 80 ? '#4caf50' : fitPercent >= 50 ? '#ff9800' : '#f44336';
                                    return (
                                      <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                            FIT {clubName}:
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
                                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', overflow: 'hidden', minWidth: 40 }}>
                                              <Box sx={{ width: `${fitPercent}%`, height: '100%', borderRadius: 3, bgcolor: fitColor, transition: 'width 0.3s' }} />
                                            </Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: fitColor, fontSize: '0.7rem' }}>
                                              {fitYes}/{fitTotal}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        {fitItems.map((f, i) => (
                                          <Chip
                                            key={i}
                                            label={`${f.label} ${f.val ? '✓' : '✗'}`}
                                            size="small"
                                            sx={{
                                              height: 22, fontSize: '0.7rem', fontWeight: 600,
                                              backgroundColor: f.val ? '#e8f5e9' : '#ffebee',
                                              color: f.val ? '#2e7d32' : '#c62828',
                                              border: '1px solid',
                                              borderColor: f.val ? '#4caf50' : '#f44336'
                                            }}
                                          />
                                        ))}
                                      </>
                                    );
                                  })()
                                )}
                                {row.stats_url && (
                                  <Chip
                                    icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                    label={t('playerCardCreator.informeForm.statsUrl')}
                                    size="small"
                                    onClick={() => { setStatsDialogUrl(row.stats_url); setIframeError(false); setStatsDialogOpen(true); }}
                                    sx={{
                                      height: 22, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                                      backgroundColor: '#e3f2fd', color: '#1565c0',
                                      border: '1px solid #1976d2',
                                      '&:hover': { backgroundColor: '#bbdefb' }
                                    }}
                                  />
                                )}
                              </Paper>
                              )}

                              {/* Bloc 2 — Versatilidad Defensiva */}
                              {hasAnyValue(normalizeVersatility(row.defensive_versatility)) && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    mt: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`
                                  }}
                                >
                                  <DefensiveVersatilityChart value={row.defensive_versatility} showTitle />
                                </Paper>
                              )}

                              {/* Bloc 3 — Détalles + média */}
                              {(row.detalles || row.media_filename) && (
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2,
                                  mt: 2,
                                  borderRadius: 2,
                                  border: `1px solid ${theme.palette.divider}`
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                  <DescriptionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {t('rechercheInforme.informeTitle')}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body1"
                                  component="div"
                                  dangerouslySetInnerHTML={{ __html: row.detalles }}
                                  sx={{
                                    '& p': { marginBottom: 1.5 },
                                    '& br': { display: 'block', content: '""', marginTop: 0.5 },
                                  }}
                                />

                                {/* Media attachment display */}
                                {row.media_filename && (
                                  <Box sx={{ mt: 2 }}>
                                  {isImageMedia(row.media_filename) ? (
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        cursor: 'pointer',
                                        '&:hover .media-overlay': { opacity: 1 }
                                      }}
                                      onClick={() => window.open(getInformeMediaUrl(row.media_filename), '_blank')}
                                    >
                                      <Box
                                        component="img"
                                        src={getInformeMediaUrl(row.media_filename)}
                                        alt="Media"
                                        sx={{
                                          maxWidth: '100%',
                                          maxHeight: 300,
                                          borderRadius: 2,
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                      />
                                      <Box
                                        className="media-overlay"
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          backgroundColor: 'rgba(0,0,0,0.4)',
                                          borderRadius: 2,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          opacity: 0,
                                          transition: 'opacity 0.2s'
                                        }}
                                      >
                                        <OpenInNewIcon sx={{ color: 'white', fontSize: 32 }} />
                                      </Box>
                                    </Box>
                                  ) : isPdfMedia(row.media_filename) ? (
                                    <Button
                                      variant="outlined"
                                      startIcon={<PictureAsPdfIcon />}
                                      endIcon={<OpenInNewIcon />}
                                      onClick={() => window.open(getInformeMediaUrl(row.media_filename), '_blank')}
                                      sx={{
                                        borderColor: '#f44336',
                                        color: '#f44336',
                                        '&:hover': {
                                          borderColor: '#d32f2f',
                                          backgroundColor: alpha('#f44336', 0.05)
                                        }
                                      }}
                                    >
                                      {t('playerCardCreator.informeTab.viewPdf')}
                                    </Button>
                                  ) : (
                                    <Chip
                                      icon={<AttachFileIcon />}
                                      label={row.media_filename}
                                      onClick={() => window.open(getInformeMediaUrl(row.media_filename), '_blank')}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  )}
                                </Box>
                              )}
                              </Paper>
                              )}

                              {/* Clips vidéo liés à l'informe */}
                              <InformeClipsSection informeId={row.id} expanded={!!openRows[row.id]} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination (desktop) */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: t('rechercheInforme.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* === Dialog d'édition === */}
      <Dialog
        open={openEditDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Empêche la fermeture sur clic hors du dialogue
          }
          handleCloseEditDialog();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('rechercheInforme.editReport')}</DialogTitle>
        <DialogContent>
          {currentInforme && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('rechercheInforme.date')}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    freeSolo
                    options={eventos}
                    value={editedEvento}
                    onChange={(_, newValue) => setEditedEvento(newValue || '')}
                    onInputChange={(event, newInputValue) => {
                      if (event && event.type === 'change') {
                        setEditedEvento(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('rechercheInforme.event')}
                        variant="outlined"
                        placeholder={t('rechercheInforme.selectOrTypeEvent')}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    freeSolo
                    options={[...new Set([...(existingEquipos || []), ...(teams || [])])].sort()}
                    value={editedEquipo}
                    getOptionLabel={(option) => option || ''}
                    isOptionEqualToValue={(option, value) => option === value}
                    onChange={(_, newValue) => setEditedEquipo(newValue)}
                    onInputChange={(event, newInputValue) => {
                      if (event && event.type === 'change') {
                        setEditedEquipo(newInputValue);
                      }
                    }}
                    renderOption={(props, option) => <li {...props}>{option}</li>}
                    renderInput={(params) => (
                      <TextField {...params} label={t('rechercheInforme.team')} variant="outlined" placeholder={t('rechercheInforme.selectOrTypeTeam')} fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label={t('rechercheInforme.scouting')}
                    value={editedVisu}
                    onChange={(e) => setEditedVisu(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="live">Live</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label={t('createPlayer.fields.currentVbcRating', { clubName })}
                    value={editedNivel}
                    onChange={(e) => setEditedNivel(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {['lowLevelProspect','midLevelProspect','goodLevelProspect','topProspect','futureStar','ebaLow','ebaHigh','lebLow','lebHigh','acbRotation','bottomBclEurocup','midBclEurocup','topBclEurocup','elMid','elTop','nbaRotation','nbaAllStar','collegeLow','collegeMid','collegeTop'].map(key => (
                      <MenuItem key={key} value={key}>{t(`createPlayer.levels.${key}`)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label={t('createPlayer.fields.projectionVbcRating', { clubName })}
                    value={editedProjection}
                    onChange={(e) => setEditedProjection(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {['lowLevelProspect','midLevelProspect','goodLevelProspect','topProspect','futureStar','ebaLow','ebaHigh','lebLow','lebHigh','acbRotation','bottomBclEurocup','midBclEurocup','topBclEurocup','elMid','elTop','nbaRotation','nbaAllStar','collegeLow','collegeMid','collegeTop'].map(key => (
                      <MenuItem key={key} value={key}>{t(`createPlayer.levels.${key}`)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Stats URL */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('playerCardCreator.informeForm.statsUrl')}
                    value={editedStatsUrl}
                    onChange={(e) => setEditedStatsUrl(e.target.value)}
                    placeholder="https://..."
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* FIT */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 1 }}>
                      FIT {clubName} :
                    </Typography>
                    {[
                      { key: 'fitEstilo', value: editedFitEstilo, setter: setEditedFitEstilo, label: t('playerCardCreator.informeForm.fitEstilo') },
                      { key: 'fitFisico', value: editedFitFisico, setter: setEditedFitFisico, label: t('playerCardCreator.informeForm.fitFisico') },
                      { key: 'fitActitud', value: editedFitActitud, setter: setEditedFitActitud, label: t('playerCardCreator.informeForm.fitActitud') },
                      { key: 'fitMejora', value: editedFitMejora, setter: setEditedFitMejora, label: t('playerCardCreator.informeForm.fitMejora') },
                      { key: 'fitContexto', value: editedFitContexto, setter: setEditedFitContexto, label: t('playerCardCreator.informeForm.fitContexto') }
                    ].map(({ key, value, setter, label }) => (
                      <Chip
                        key={key}
                        label={`${label}: ${value === true ? t('playerCardCreator.informeForm.fitYes') : value === false ? t('playerCardCreator.informeForm.fitNo') : '—'}`}
                        onClick={() => {
                          if (value === null) setter(true);
                          else if (value === true) setter(false);
                          else setter(null);
                        }}
                        sx={{
                          cursor: 'pointer', fontWeight: 600,
                          backgroundColor: value === true ? '#e8f5e9' : value === false ? '#ffebee' : '#f5f5f5',
                          color: value === true ? '#2e7d32' : value === false ? '#c62828' : '#9e9e9e',
                          border: '1px solid',
                          borderColor: value === true ? '#4caf50' : value === false ? '#f44336' : '#e0e0e0',
                          '&:hover': { opacity: 0.85 }
                        }}
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <DefensiveVersatilityEditor
                    value={editedDefensiveVersatility}
                    onChange={setEditedDefensiveVersatility}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                    <strong>{t('rechercheInforme.details')}:</strong>
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={editedDetalles}
                    onChange={setEditedDetalles}
                    placeholder={t('rechercheInforme.editDetailsPlaceholder')}
                  />
                </Grid>

                {/* Media Upload Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, mt: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachFileIcon sx={{ fontSize: 18 }} />
                    {t('playerCardCreator.informeForm.attachMedia')}
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      ({t('playerCardCreator.informeForm.optional')})
                    </Typography>
                  </Typography>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
                        if (!validTypes.includes(file.type)) {
                          alert(t('playerCardCreator.informeForm.invalidFileType'));
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) {
                          alert(t('playerCardCreator.informeForm.fileTooLarge'));
                          return;
                        }
                        setEditedMediaFile(file);
                        setRemoveExistingMedia(false);
                      }
                    }}
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    style={{ display: 'none' }}
                  />

                  {/* Show current media if exists and not removed */}
                  {currentInforme?.media_filename && !removeExistingMedia && !editedMediaFile && (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: 'grey.50'
                      }}
                    >
                      {isImageMedia(currentInforme.media_filename) ? (
                        <Box
                          component="img"
                          src={getInformeMediaUrl(currentInforme.media_filename)}
                          alt="Media"
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f44336',
                            borderRadius: 1
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {currentInforme.media_filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('playerCardCreator.informeTab.currentMedia')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => editFileInputRef.current?.click()}
                        >
                          {t('playerCardCreator.informeTab.changeMedia')}
                        </Button>
                        <IconButton
                          onClick={() => setRemoveExistingMedia(true)}
                          size="small"
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                  )}

                  {/* Show new media file preview */}
                  {editedMediaFile && (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }}
                    >
                      {editedMediaPreviewUrl ? (
                        <Box
                          component="img"
                          src={editedMediaPreviewUrl}
                          alt="Preview"
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}
                        />
                      ) : editedMediaFile.type === 'application/pdf' ? (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f44336',
                            borderRadius: 1
                          }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.200',
                            borderRadius: 1
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {editedMediaFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(editedMediaFile.size / 1024).toFixed(1)} KB • {t('playerCardCreator.informeTab.newMedia')}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => {
                          setEditedMediaFile(null);
                          if (editFileInputRef.current) editFileInputRef.current.value = '';
                        }}
                        size="small"
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}

                  {/* Show upload zone if no media */}
                  {((!currentInforme?.media_filename || removeExistingMedia) && !editedMediaFile) && (
                    <Box
                      onClick={() => editFileInputRef.current?.click()}
                      sx={{
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: 'grey.50',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('playerCardCreator.informeForm.dropOrClick')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('playerCardCreator.informeForm.pasteHint')}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          JPG, PNG, GIF, WebP, PDF • Max 10MB
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="secondary">
            {t('rechercheInforme.cancel')}
          </Button>
          <Button onClick={handleEditSave} color="primary" variant="contained">
            {t('rechercheInforme.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* === Dialog de suppression === */}
      <Dialog
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">{t('rechercheInforme.deleteReport')}</DialogTitle>
        <DialogContent>
          <Typography>{t('rechercheInforme.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="secondary">
            {t('rechercheInforme.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="primary" variant="contained">
            {t('rechercheInforme.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats URL iframe dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            {t('playerCardCreator.informeForm.statsUrl')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(statsDialogUrl, '_blank')}
            >
              {t('playerCardCreator.informeForm.openInNewTab')}
            </Button>
            <IconButton onClick={() => setStatsDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {iframeError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <Typography color="text.secondary">
                {t('playerCardCreator.informeForm.iframeBlocked')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(statsDialogUrl, '_blank')}
              >
                {t('playerCardCreator.informeForm.openInNewTab')}
              </Button>
            </Box>
          ) : (
            <iframe
              src={statsDialogUrl}
              title="Stats"
              style={{ width: '100%', height: '100%', border: 'none' }}
              onError={() => setIframeError(true)}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RechercheInforme;
